<template>
	<widget-settings-layout ref="layout" v-if="settings" slug="viewerCount" name="Viewer count" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card title="Layout" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Orientation">
						<b-button-group class="d-flex">
							<b-button :pressed="settings.orientation !== 'vertical'" @click="settings.orientation = 'horizontal'" variant="outline-secondary">Horizontal</b-button>
							<b-button :pressed="settings.orientation === 'vertical'" @click="settings.orientation = 'vertical'" variant="outline-secondary">Vertical</b-button>
						</b-button-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Logo position">
						<b-button-group class="d-flex">
							<b-button :pressed="settings.logoPosition !== 'right'" @click="settings.logoPosition = 'left'" variant="outline-secondary">Left</b-button>
							<b-button :pressed="settings.logoPosition === 'right'" @click="settings.logoPosition = 'right'" variant="outline-secondary">Right</b-button>
						</b-button-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Sort">
						<b-form-select v-model="settings.sort" :options="sortOptions"></b-form-select>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group :label="settings.orientation === 'vertical' ? 'Vertical spacing' : 'Horizontal spacing'">
						<b-form-select v-model="settings.justifyContent" :options="justifyOptions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group :label="settings.orientation === 'vertical' ? 'Horizontal alignment' : 'Vertical alignment'">
						<b-button-group class="d-flex">
							<b-button v-b-tooltip.hover.bottom title="Top"    :pressed="settings.alignItems === 'flex-start'" @click="settings.alignItems = 'flex-start'" variant="outline-secondary"><b-icon icon="align-top"    /></b-button>
							<b-button v-b-tooltip.hover.bottom title="Middle" :pressed="settings.alignItems === 'center'"     @click="settings.alignItems = 'center'"     variant="outline-secondary"><b-icon icon="align-middle" /></b-button>
							<b-button v-b-tooltip.hover.bottom title="Bottom" :pressed="settings.alignItems === 'flex-end'"   @click="settings.alignItems = 'flex-end'"   variant="outline-secondary"><b-icon icon="align-bottom" /></b-button>
						</b-button-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3" title="Show">
			<b-form-checkbox v-model="settings.showDisabledChannels">Disabled channels</b-form-checkbox>
			<b-form-checkbox v-model="settings.showOfflineChannels">Offline channels</b-form-checkbox>
			<b-form-checkbox v-model="settings.showOnlyVerifiedChannels">Only verified channels</b-form-checkbox>
		</b-card>
		<hide-channels :channels="channels" flag="no-viewer-count" disabled-field="no_streaming" disabled-label="not applicable (no streaming)" @toggle="toggleChannelFlag" />
		<text-settings :settings="settings" no-align no-uppercase />
		<b-card title="Outline" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Blur radius">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model.number="settings.blurRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Spread radius">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model.number="settings.spreadRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Outline Color">
						<color-picker v-model="settings.shadowColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Colors" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text Color">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Item Background">
						<color-picker v-model="settings.textBg" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<b-form-group label="Extra CSS" class="mb-0">
				<b-textarea v-model="settings.extraCSS"></b-textarea>
			</b-form-group>
		</b-card>
		<template #preview>
			<viewer-count-widget-preview :settings="settings" />
		</template>
		<template #save-extra>
			<b-button @click="copyStyleFromChatWidget()" :disabled="copyingStyle" class="ml-3">Copy style from chat widget</b-button>
		</template>
	</widget-settings-layout>
</template>

<script>
import ColorPicker from '@/components/ColorPicker.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import TextSettings from '@/components/Widget/TextSettings.vue';
import HideChannels from '@/components/Widget/HideChannels.vue';
import ViewerCountWidgetPreview from '@/components/Widget/ViewerCountWidgetPreview.vue';

export default {
	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		settings: null,
		channels: [],
		origChannelFlagsJson: null,
		error: null,
		loading: 0,
		saveError: null,
		saving: 0,
		copyingStyle: false,
		savedSettingsJson: null,
		sortOptions: [
			{ value: '', text: 'Name' },
			{ value: 'asc',  text: 'Ascending' },
			{ value: 'desc', text: 'Descending' },
		],
		justifyOptions: [
			{ value: 'flex-start',    text: 'Start' },
			{ value: 'center',        text: 'Center' },
			{ value: 'flex-end',      text: 'End' },
			{ value: 'space-between', text: 'Space between' },
			{ value: 'space-around',  text: 'Space around' },
			{ value: 'space-evenly',  text: 'Space evenly' },
		],
	}},
	components: {
		ColorPicker,
		ViewerCountWidgetPreview,
		WidgetSettingsLayout,
		TextSettings,
		HideChannels,
	},
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.loading++;
			this.error = null;
			this.saveError = null;
			const [s, channels] = await Promise.all([
				this.$api.get('widgetSettings/viewer-count'),
				this.$api.get('channels/widgets').catch(() => []),
			]);
			this.channels = channels.map(c => ({ ...c, flags: c.flags || [] }));
			this.origChannelFlagsJson = JSON.stringify(this.channels.map(c => c.flags));
			this.settings = {
				// Migrate legacy boolean settings to new string settings
				orientation:    s.orientation    || (s.stackViewerCounts ? 'vertical' : 'horizontal'),
				logoPosition:   s.logoPosition   || (s.logoOnRight       ? 'right'    : 'left'),
				justifyContent: s.justifyContent || 'flex-start',
				alignItems:     s.alignItems     || 'center',
				sort:           (s.sort && s.sort !== 'none') ? s.sort : '',
				showDisabledChannels:      s.showDisabledChannels      || false,
				showOfflineChannels:       s.showOfflineChannels       || false,
				showOnlyVerifiedChannels:  s.showOnlyVerifiedChannels  || false,
				textColor:    s.textColor    || 'rgba(255, 255, 255, 1)',
				shadowColor:  s.shadowColor  || 'rgba(0, 0, 0, 1)',
				textBg:       s.textBg       || 'rgba(0, 128, 0, 0)',
				blurRadius:   s.blurRadius   || 0,
				spreadRadius: s.spreadRadius || 0,
				boldText:          s.boldText          || false,
				italicText:        s.italicText        || false,
				underlineText:     s.underlineText     || false,
				strikethroughText: s.strikethroughText || false,
				extraCSS:     s.extraCSS     || '',
				fontFamily:   s.fontFamily   || '',
				fontSize:     s.fontSize     || 0,
				letterSpacing: s.letterSpacing || 0,
				customFont:   s.customFont   !== undefined ? s.customFont : true,
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	computed: {
		hasUnsavedChanges() {
			if(this.savedSettingsJson === null) return false;
			if(JSON.stringify(this.settings) !== this.savedSettingsJson) return true;
			if(this.origChannelFlagsJson !== null && JSON.stringify(this.channels.map(c => c.flags)) !== this.origChannelFlagsJson) return true;
			return false;
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
	},
	methods: {
		_onBeforeUnload(e) {
			if (this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		async save() {
			try {
				this.saving++;
				this.saveError = null;
				await Promise.all([
					this.$api.post('widgetSettings/viewer-count', { ...this.settings }),
					...this.channels.map(c => this.$api.patch(`channel/${encodeURIComponent(c.service_id)}/${encodeURIComponent(c.token)}`, { flags: c.flags })),
				]);
				this.savedSettingsJson = JSON.stringify(this.settings);
				this.origChannelFlagsJson = JSON.stringify(this.channels.map(c => c.flags));
			} catch(e) {
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
		toggleChannelFlag(channel, flag, checked) {
			const flags = [...(channel.flags || [])];
			const idx = flags.indexOf(flag);
			if(checked && idx === -1) flags.push(flag);
			else if(!checked && idx !== -1) flags.splice(idx, 1);
			this.$set(channel, 'flags', flags);
		},
		async copyStyleFromChatWidget() {
			try {
				this.loading++;
				this.copyingStyle = true;
				this.error = null;
				const s = await this.$api.get('widgetSettings/chat');
				this.settings.blurRadius      = s.blurRadius      || 0;
				this.settings.spreadRadius    = s.spreadRadius    || 0;
				this.settings.boldText        = s.boldText        || false;
				this.settings.extraCSS        = s.extraCSS        || '';
				this.settings.fontFamily      = s.fontFamily      || '';
				this.settings.fontSize        = s.fontSize        || 0;
				this.settings.italicText      = s.italicText      || false;
				this.settings.letterSpacing   = s.letterSpacing   || 0;
				this.settings.shadowColor     = s.shadowColor     || 'rgba(0, 0, 0, 1)';
				this.settings.strikethroughText = s.strikethroughText || false;
				this.settings.textBg          = s.textBg          || 'rgba(0, 128, 0, 0)';
				this.settings.textColor       = s.textColor       || 'rgba(255, 255, 255, 1)';
				this.settings.underlineText   = s.underlineText   || false;
				this.settings.customFont      = s.customFont      || false;
			} catch(e) {
				this.error = e;
			} finally {
				this.copyingStyle = false;
				this.loading--;
			}
		},
	},
};
</script>
